
import { Vue, Component } from 'vue-property-decorator'
import { ApolloError } from 'apollo-boost'
import FlashMessageBox from '@mg/web-components/Atoms/FlashMessageBox'
import { FlashMessage } from '@mg/web-components/Atoms/FlashMessageBox/types'
import PageTitle from '~/components/PageTitle.vue'
import { trackWebKcPageView } from '~/utils/tracking/gtm/pageViews'
import PersonalDataFormsAccordion from '~/components/personalDataForms/PersonalDataFormsAccordion.vue'
import {
  PersonalDataFormType,
  PersonalDataFormSubmitData,
} from '~/components/personalDataForms/types'
import {
  GET_PERSONAL_DATA_FORM_TYPES,
  SUBMIT_PERSONAL_DATA_REQUEST,
} from '~/graphql/gql/personalDataForms'
import { ApiValidationErrors } from '~/utils/apolloErrors'
import { ROUTE_PATHS } from '~/config/routePaths'
import { Shops } from '~/config/shops'

@Component<PersonalDataFormsPage>({
  components: {
    PersonalDataFormsAccordion,
    PageTitle,
    FlashMessageBox,
  },
  apollo: {
    personalDataFormTypes: {
      query: GET_PERSONAL_DATA_FORM_TYPES,
      errorPolicy: 'all',
      skip() {
        return process.server && !this.$shopConfig.isBot
      },
      result({ data }) {
        if (data)
          trackWebKcPageView(
            this,
            this.$t('personaldataforms.page_title') as string
          )
      },
      error() {
        this.formsLoadError = true
      },
    },
  },
})
export default class PersonalDataFormsPage extends Vue {
  personalDataFormTypes: PersonalDataFormType[] = []
  formsLoadError = false
  isLoading = false
  errorMessage: string | null = null
  apiErrors: ApiValidationErrors = {}

  get flashMessageError(): FlashMessage | null {
    if (!this.errorMessage) return null
    const message = this.$toaster.createErrorMessage(this.errorMessage)
    message.isToast = false
    message.timeout = 0
    return message
  }

  /**
   * Backward compatibility for old monolith URLs:
   * Old: /{lang-slug}?do=verify&hash={hash}
   * New: /{lang-slug-verify}/{hash}
   *
   * Supported legacy entry URLs (all redirect to the verify route):
   *   CZ: /sprava-osobnich-udaju?do=verify&hash={hash}         → /sprava-osobnich-udaju/overeni/{hash}
   *   SK: /sprava-osobnych-udajov?do=verify&hash={hash}        → /sprava-osobnych-udajov/overenie/{hash}
   *   PL: /zarzadzanie-danymi-osobowymi?do=verify&hash={hash}  → /zarzadzanie-danymi-osobowymi/weryfikacja/{hash}
   *   HU: /szemelyes-adatok-kezelese?do=verify&hash={hash}     → /szemelyes-adatok-kezelese/visszaigazolas/{hash}
   *   HR: /uredivanje-osobnih-podataka?do=verify&hash={hash}   → /uredivanje-osobnih-podataka/potvrda/{hash}
   *   SI: /urejanje-osebnih-podatkov?do=verify&hash={hash}     → /urejanje-osebnih-podatkov/potrditev/{hash}
   *
   * Alias URLs (all shops, redirect to localized canonical via middleware/routing.ts):
   *   /personal-data-forms/verify/{hash}  → /{lang-slug-verify}/{hash}
   */
  mounted() {
    if (this.$route.query.do === 'verify' && this.$route.query.hash) {
      const hash = this.$route.query.hash as string
      const shopId = this.$shopConfig.shopId as Shops
      const verifyPathPattern =
        ROUTE_PATHS.personalDataFormsVerify.paths[shopId]
      if (verifyPathPattern) {
        this.$router.replace(`/${verifyPathPattern.replace(':hash', hash)}`)
      }
    }
  }

  get prefillData() {
    if (this.$shopConfig.user?.email) {
      return {
        firstname: '',
        lastname: '',
        email: this.$shopConfig.user.email || '',
      }
    }
    return null
  }

  get formTranslations() {
    return {
      firstnameLabel: this.$t('personaldataforms.form_firstname_label'),
      lastnameLabel: this.$t('personaldataforms.form_lastname_label'),
      emailLabel: this.$t('personaldataforms.form_email_label'),
      requestTextLabel: this.$t('personaldataforms.form_request_text_label'),
      submitButton: this.$t('personaldataforms.form_submit_button'),
      recaptchaValidationError: this.$t(
        'personaldataforms.form_recaptcha_validation_error'
      ),
      validationRequired: this.$t(
        'personaldataforms.form_validation_error_required_field'
      ),
      validationEmail: this.$t('personaldataforms.form_validation_error_email'),
      validationInvalidChars: this.$t(
        'personaldataforms.form_validation_error_invalid_field'
      ),
      errorGeneral: this.$t('personaldataforms.form_error_general'),
    }
  }

  async handleSubmit(data: PersonalDataFormSubmitData) {
    this.isLoading = true
    this.errorMessage = null
    this.apiErrors = {}

    try {
      const { data: result } = await this.$apollo.mutate({
        mutation: SUBMIT_PERSONAL_DATA_REQUEST,
        variables: {
          input: {
            firstname: data.firstname,
            lastname: data.lastname,
            email: data.email,
            requestText: data.requestText || '',
            // recaptchaToken: 'aa',
            recaptchaToken: data.recaptchaToken,
            typeId: data.typeId,
          },
        },
      })

      if (result?.submitPersonalDataRequest?.success) {
        this.$router.push({
          name: 'personalDataFormsSent',
          query: { email: result.submitPersonalDataRequest.email },
        })
      } else {
        this.errorMessage =
          result?.submitPersonalDataRequest?.error ||
          (this.$t('personaldataforms.form_error_general') as string)
      }
    } catch (error) {
      const apolloError = error as ApolloError
      const isRecaptchaError = apolloError.graphQLErrors?.some(
        (e) => e.extensions?.code === 'UNAUTHENTICATED'
      )
      this.errorMessage = isRecaptchaError
        ? (this.$t(
            'personaldataforms.form_recaptcha_validation_error'
          ) as string)
        : (this.$t('personaldataforms.form_error_general') as string)
      this.$logger.error('submitPersonalDataRequest error:', error)
    } finally {
      this.isLoading = false
    }
  }
}
